import React, { useCallback, useEffect, useState } from "react";
import ReactDOM from "react-dom";
import {
	FaGift,
	FaTimes,
	FaPalette,
	FaSearch,
	FaUndo,
	FaRegClone,
	FaExpandArrowsAlt,
	FaMoon,
	FaCode,
	FaKeyboard,
} from "react-icons/fa";
import "./WhatsNew.css";

/**
 * Modal "Novidades" (ticket 13 — .scratch/builder-ux).
 *
 * Aparece centralizado automaticamente na PRIMEIRA vez que o usuário abre o
 * builder novo (persistido em localStorage — precisa sobreviver a reload, por
 * isso não é sessão-only como o resto). Depois de dispensado, não abre mais
 * sozinho; um botão "Novidades" na topbar reabre quando quiser.
 *
 * A chave é versionada: bumpar SEEN_KEY re-anuncia novas features no futuro.
 */

const SEEN_KEY = "eloz_builder_whatsnew_seen_v1";

const ITEMS = [
	{
		icon: <FaPalette />,
		title: "Visual repaginado",
		desc: "Builder com a nova cara rvops — cards, topbar, paleta e controles redesenhados.",
	},
	{
		icon: <FaSearch />,
		title: "Busca de ações (Ctrl/⌘ + K)",
		desc: "Ache qualquer ação por Ref, nome do evento ou pelo conteúdo interno (mensagens, webhooks, variáveis…). Ao selecionar, a tela voa até o nó. Guarda os recentes e mantém o filtro.",
	},
	{
		icon: <FaUndo />,
		title: "Desfazer / Refazer",
		desc: "Ctrl/⌘ + Z volta, Ctrl + Y (ou Ctrl/⌘ + Shift + Z) avança as edições do fluxo.",
	},
	{
		icon: <FaRegClone />,
		title: "Copiar e colar ações",
		desc: "Selecione uma ou mais ações e use Ctrl/⌘ + C / V — as conexões internas vão junto.",
	},
	{
		// "Auto-organizar" oculto por enquanto (a pedido) — texto só sobre "Espalhar".
		icon: <FaExpandArrowsAlt />,
		title: "Espalhar os nós",
		desc: "Nos controles, o botão “Espalhar” abre espaço entre as ações mantendo seu arranjo. Ótimo pra bots antigos com os cards sobrepostos.",
	},
	{
		icon: <FaMoon />,
		title: "Fundo claro ou escuro",
		desc: "Alterne o fundo do canvas pelo botão nos controles — o mesmo vale no /analytics.",
	},
	{
		icon: <FaCode />,
		title: "Variáveis em painel lateral",
		desc: "Gerencie as variáveis num drawer dedicado, com busca — e o seletor {{variável}} dos campos também ganhou busca.",
	},
	{
		icon: <FaKeyboard />,
		title: "Dica de atalhos",
		desc: "O ícone de teclado na topbar lista todos os atalhos a qualquer momento.",
	},
];

export default function WhatsNew() {
	const [open, setOpen] = useState(false);

	// Auto-abre na primeira vez (não visto ainda).
	useEffect(() => {
		let seen = false;
		try {
			seen = localStorage.getItem(SEEN_KEY) === "1";
		} catch (e) {
			seen = false;
		}
		if (!seen) setOpen(true);
	}, []);

	const markSeen = useCallback(() => {
		try {
			localStorage.setItem(SEEN_KEY, "1");
		} catch (e) {
			/* localStorage indisponível (aba privada etc.) — ignora */
		}
	}, []);

	const close = useCallback(() => {
		markSeen();
		setOpen(false);
	}, [markSeen]);

	// Esc fecha.
	useEffect(() => {
		if (!open) return;
		const handler = (e) => {
			if ((e.key || "").toLowerCase() === "escape") close();
		};
		window.addEventListener("keydown", handler);
		return () => window.removeEventListener("keydown", handler);
	}, [open, close]);

	return (
		<div className="whatsnew">
			<button
				type="button"
				className="whatsnew__trigger"
				title="Novidades da nova interface"
				onClick={() => setOpen(true)}
			>
				<FaGift />
				<span>Novidades</span>
			</button>

			{open &&
				ReactDOM.createPortal(
					<div className="whatsnew__backdrop" onClick={close}>
						<div
							className="whatsnew__modal"
							role="dialog"
							aria-modal="true"
							aria-label="Novidades da interface"
							onClick={(e) => e.stopPropagation()}
						>
						<button
							type="button"
							className="whatsnew__close"
							onClick={close}
							title="Fechar"
							aria-label="Fechar"
						>
							<FaTimes />
						</button>

						<div className="whatsnew__header">
							<div className="whatsnew__badge">
								<FaGift />
							</div>
							<h2 className="whatsnew__title">Novidades no builder ✨</h2>
							<p className="whatsnew__subtitle">
								Demos uma repaginada geral e adicionamos várias facilidades.
								Dá uma olhada:
							</p>
						</div>

						<ul className="whatsnew__list">
							{ITEMS.map((item, i) => (
								<li className="whatsnew__item" key={i}>
									<span className="whatsnew__itemIcon">{item.icon}</span>
									<div className="whatsnew__itemBody">
										<span className="whatsnew__itemTitle">{item.title}</span>
										<span className="whatsnew__itemDesc">{item.desc}</span>
									</div>
								</li>
							))}
						</ul>

						<div className="whatsnew__footer">
							<button
								type="button"
								className="whatsnew__cta"
								onClick={close}
							>
								Entendi, vamos lá
							</button>
						</div>
					</div>
				</div>,
				document.body
			)}
		</div>
	);
}
